import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { HT_FORM_KEY } from '../packinglist/ht/htCertificadoNr';
import { urlApiProd } from '../utiles';

/**
 * Único campo editable del certificado HT: Nr. (E13).
 * El resto se obtiene de invoice, packing list y textos fijos de plantilla.
 */
const HtCertificadoModal = ({ show, row, onClose, onSaved, authHeaders }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certificadoNr, setCertificadoNr] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !row?.id_invoice) return;
    const headers = authHeaders?.();
    if (!headers) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}`, { headers });
        if (!res.ok) throw new Error('No se pudo cargar la invoice.');
        const inv = await res.json();
        if (cancelled) return;
        const f = inv.datos_formulario && typeof inv.datos_formulario === 'object'
          ? inv.datos_formulario
          : {};
        const ht = f[HT_FORM_KEY] || {};
        setCertificadoNr(ht.certificadoNr == null ? '' : String(ht.certificadoNr));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, row?.id_invoice, authHeaders]);

  const handleSave = async () => {
    const headers = authHeaders?.();
    if (!headers || !row?.id_invoice) return;
    setSaving(true);
    setError('');
    try {
      const getRes = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}`, { headers });
      if (!getRes.ok) throw new Error('No se pudo cargar la invoice.');
      const inv = await getRes.json();
      const datos = {
        ...(inv.datos_formulario && typeof inv.datos_formulario === 'object'
          ? inv.datos_formulario
          : {}),
        [HT_FORM_KEY]: {
          certificadoNr: certificadoNr.trim(),
        },
      };
      const putRes = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numero_invoice: inv.numero_invoice,
          fecha_invoice: inv.fecha_invoice,
          nombre_bl: inv.nombre_bl,
          nombre_cliente: inv.nombre_cliente,
          pais_destino: inv.pais_destino,
          valor_usd: inv.valor_usd,
          datos_formulario: datos,
        }),
      });
      if (!putRes.ok) {
        const err = await putRes.json().catch(() => ({}));
        throw new Error(err.error || 'No se pudo guardar.');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Certificado HT — Nr.</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Solo el número del certificado (celda Nr. de la hoja HT). Consignatario, origen, destino,
          pesos, BL, fechas y cantidades salen de la invoice y el packing list.
        </p>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <Form.Group>
            <Form.Label>Nr. certificado fitossanitario</Form.Label>
            <Form.Control
              type="text"
              value={certificadoNr}
              onChange={(e) => setCertificadoNr(e.target.value)}
              placeholder="ej. 131174/26 AD"
              disabled={saving}
            />
            <Form.Text className="text-muted">En el documento se muestra como «Nr.: …»</Form.Text>
          </Form.Group>
        )}
        {error ? <p className="text-danger small mt-2 mb-0">{error}</p> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="dark" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HtCertificadoModal;
